import { useCallback } from "react";
import { TellescopeMutationInlineOptions } from "../../types";
import { useTellescopeMutation } from "../../use-tellescope-mutation";

/** Just the values needed from the caller on mutate */
export interface SESDeleteDomainRequest {
  domain: string;
}

export interface SESDeleteDomainResponse {
  data: {
    success: true;
  };
}

type DeleteDomainInlineOptions = TellescopeMutationInlineOptions<"integrations", "proxy_write">;

/**
 * Deletes a self-serve email sending domain (AWS SES). Only domains whose status is `pending` or
 * `failed` can be deleted — the backend rejects verified domains. On success the org is optimistically
 * updated to mirror the server-side changes (the domain is removed from `verifiedEmailDomains` and any
 * `*@domain` aliases are stripped from `fromEmails`) via the standard mutation side-effect mappings for
 * the `integrations` model. The org update is rolled back automatically on error.
 */
export const useSESDeleteDomainMutation = () => {
  const mutation = useTellescopeMutation({
    model: "integrations",
    method: "proxy_write",
    autoCacheUpdateStrategy: "update",
    optimisticUpdates: true,
  });

  const mutate = useCallback(
    (request: SESDeleteDomainRequest, options?: DeleteDomainInlineOptions) =>
      mutation.mutate({ integration: "SES", type: "delete-domain", id: request.domain }, options),
    [mutation]
  );

  const mutateAsync = useCallback(
    (request: SESDeleteDomainRequest, options?: DeleteDomainInlineOptions) =>
      mutation.mutateAsync({ integration: "SES", type: "delete-domain", id: request.domain }, options),
    [mutation]
  );

  return { ...mutation, mutate, mutateAsync };
};
